<script setup>
import { computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import CampaignReport from 'dashboard/components-next/Campaigns/Report/CampaignReport.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useCampaignReport } from 'dashboard/composables/useCampaignReport';

const props = defineProps({
  listRouteName: {
    type: String,
    required: true,
  },
  campaignParam: {
    type: [String, Number],
    required: true,
  },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const campaigns = useMapGetter('campaigns/getAllCampaigns');

const { reportData, isLoadingReport, loadReport, resetReport } =
  useCampaignReport();

const campaignParam = computed(() => props.campaignParam?.toString() || '');

const slugify = value =>
  value
    ?.toString()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || '';

const paramId = computed(() => {
  const match = campaignParam.value.match(/^\d+/);
  return match ? match[0] : null;
});

const paramSlug = computed(() => {
  const slugPart = campaignParam.value.replace(/^\d+-?/, '');
  return slugPart || null;
});

const campaignById = computed(() => {
  if (!paramId.value) return null;
  const list = campaigns.value || [];
  return list.find(campaign => String(campaign.id) === paramId.value) || null;
});

const campaignBySlug = computed(() => {
  if (!paramSlug.value) return null;
  const list = campaigns.value || [];
  return (
    list.find(campaign => slugify(campaign.title) === paramSlug.value) || null
  );
});

const activeCampaign = computed(() => {
  return campaignById.value || campaignBySlug.value || null;
});

const requestCampaignId = computed(() => {
  if (paramId.value) return paramId.value;
  return activeCampaign.value ? activeCampaign.value.id : null;
});

const summary = computed(() => ({
  sent: reportData.value?.sent ?? activeCampaign.value?.it_sent ?? 0,
  failed: reportData.value?.failed ?? activeCampaign.value?.failed ?? 0,
  processed:
    reportData.value?.processed ?? activeCampaign.value?.status_send ?? 0,
}));

const contacts = computed(() => reportData.value?.contacts ?? []);

const hasData = computed(() => !!activeCampaign.value || !!reportData.value);

const ensureCampaignsLoaded = async () => {
  if (!campaigns.value || campaigns.value.length === 0) {
    await store.dispatch('campaigns/get');
  }
};

watch(
  requestCampaignId,
  id => {
    if (id) {
      loadReport(id);
    } else {
      resetReport();
    }
  },
  { immediate: true }
);

watch(
  () => route.params.accountId,
  () => {
    ensureCampaignsLoaded();
  },
  { immediate: true }
);

onMounted(() => {
  ensureCampaignsLoaded();
});

onBeforeUnmount(() => {
  resetReport();
});

const goBack = () => {
  router.push({
    name: props.listRouteName,
    params: {
      accountId: route.params.accountId,
    },
  });
};

const handleClose = () => {
  goBack();
};

const showMissingState = computed(
  () => !isLoadingReport.value && !hasData.value
);
</script>

<template>
  <section class="flex flex-col h-full bg-n-background">
    <header class="px-4 sm:px-6 py-4 border-b border-n-weak">
      <div class="flex items-center gap-3">
        <Button
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-arrow-left"
          :aria-label="t('CAMPAIGN.REPORT.ACTIONS.BACK')"
          @click="goBack"
        >
          <span class="hidden sm:inline">{{
            t('CAMPAIGN.REPORT.ACTIONS.BACK')
          }}</span>
        </Button>
        <h1 class="text-lg font-medium text-n-slate-12">
          {{ t('CAMPAIGN.REPORT.PAGE_TITLE') }}
        </h1>
      </div>
    </header>
    <main class="flex-1 overflow-y-auto px-4 sm:px-6 py-6">
      <div v-if="isLoadingReport && !hasData" class="flex justify-center py-10">
        <Spinner />
      </div>
      <CampaignReport
        v-else-if="hasData || isLoadingReport"
        :campaign="activeCampaign"
        :summary="summary"
        :contacts="contacts"
        :is-loading="isLoadingReport"
        @close="handleClose"
      />
      <div
        v-else-if="showMissingState"
        class="flex justify-center py-10 text-sm text-n-slate-11"
      >
        {{ t('CAMPAIGN.REPORT.MISSING_CAMPAIGN') }}
      </div>
    </main>
  </section>
</template>
